/**
 * 체험단 캠페인 수정 페이지
 *
 * - 기존 캠페인 데이터를 불러와 폼에 pre-fill
 * - react-hook-form + zod 스키마로 폼 검증
 * - 상태별 수정 제한:
 *   - draft/scheduled: 모든 필드 수정 가능
 *   - recruiting~in_progress: 상품/수집항목/로그인 설정 잠금
 *   - completed: 전체 읽기전용
 * - 저장 시 updateCampaign API 호출
 * - 공통 폼은 form/CampaignForm 사용
 */
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/Button'
import {
  getCampaign,
  useUpdateCampaign,
  getGetCampaignQueryKey,
  getListCampaignsQueryKey,
} from '@/api/generated/v2-seeding-campaigns/v2-seeding-campaigns'
import { getProducts } from '@/api/generated/v2-catalog/v2-catalog'
import { auth } from '@/lib/auth'
import { useShop } from '@/lib/shopContext'
import {
  DEFAULT_FIELD_CONFIG,
  type SeedingCampaignResponse,
  type SeedingCampaignUpdateRequest,
  type MissionRound,
  type DeliveryMethod,
} from '@/types'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { useConfirm } from '@/hooks/useConfirm'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

// 폼 컴포넌트 (features/ 디렉토리로 이동됨)
import { CampaignForm } from '@/features/seeding/form/CampaignForm'
import { SeedingPublishGate, extractGateMessage } from '@/features/seeding/SeedingPublishGate'
import { dateStrToIso, isoToDateStr } from '@/features/seeding/form/utils'
import { campaignFormSchema } from '@/features/seeding/form/schema'
import type { CampaignFormValues } from '@/features/seeding/form/schema'
import type { CampaignFormData, RoundFormData, ProductSelectionState } from '@/features/seeding/form/types'

export const Route = createFileRoute('/_dashboard/seeding-campaigns/$campaignId/edit')({
  component: EditCampaign,
})

function EditCampaign() {
  const { campaignId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // shop 은 레이아웃이 useGetShop 으로 채운 컨텍스트에서 읽는다 (여기서 재조회하지 않는다)
  const { shop } = useShop()
  const [campaign, setCampaign] = useState<SeedingCampaignResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // 공개된 캠페인 수정 시 연락처 미등록 게이트(422) 안내 메시지. null이면 닫아둔다.
  const [gateMessage, setGateMessage] = useState<string | null>(null)

  // Orval mutation 훅 — 캠페인 수정
  const updateCampaignMutation = useUpdateCampaign()
  const { confirm, ConfirmDialog } = useConfirm()

  // react-hook-form 인스턴스 — zod 스키마로 검증
  // zodResolver 타입 단언: zod v4와 @hookform/resolvers v5의 타입 호환 문제 우회
  const form = useForm<CampaignFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(campaignFormSchema as any) as any,
    defaultValues: {}, // 로딩 후 reset으로 설정
    mode: 'onSubmit',
    // 자동 포커스는 CampaignForm 이 화면 배치 순서로 직접 한다 (new.tsx 주석 참조)
    shouldFocusError: false,
  })

  // 초기 데이터 (로딩 후 설정)
  const [initialRounds, setInitialRounds] = useState<RoundFormData[]>([
    { title: '', guideline: '', guideline_files: [], deadline_at: '', mission_type: 'media' },
  ])
  const [initialProductSelection, setInitialProductSelection] = useState<Partial<ProductSelectionState>>({})
  const [initialUseProductImage, setInitialUseProductImage] = useState(false)
  const [dataReady, setDataReady] = useState(false)

  // 상태별 수정 제한
  const isLocked = !!campaign?.status && ['recruiting', 'pending_selection', 'in_progress'].includes(campaign.status)
  const isRecruiting = campaign?.status === 'recruiting'
  const isPostRecruit = !!campaign?.status && ['pending_selection', 'in_progress'].includes(campaign.status)
  const isCompleted = campaign?.status === 'completed'
  const hasApplicants = isRecruiting && (campaign?.application_count ?? 0) > 0
  const originalQuestionIds = useMemo(
    () => new Set((campaign?.extra_questions || []).map((q: { id: string }) => q.id)),
    [campaign?.extra_questions],
  )

  // 데이터 로드 및 폼 pre-fill
  useEffect(() => {
    const loadData = async () => {
      if (!auth.isAuthenticated()) {
        navigate({ to: '/' })
        return
      }

      try {
        // Orval 생성 함수로 캠페인 조회
        const campaignResponse = await getCampaign(campaignId)
        // custom-instance가 data만 반환하므로 직접 캐스팅
        const campaignData = campaignResponse as unknown as SeedingCampaignResponse
        setCampaign(campaignData)

        // 폼 데이터 pre-fill
        const prefillData: CampaignFormValues = {
          title: campaignData.title,
          description: campaignData.description || '',
          notices: campaignData.notices || '',
          thumbnail_url: campaignData.thumbnail_url || '',
          detail_images: campaignData.detail_images || [],
          delivery_method: (campaignData.delivery_method || 'cafe24_order') as DeliveryMethod,
          // 임시 저장 캠페인은 비어 있을 수 있다 — 폼은 빈 문자열/0 을 미입력으로 다룬다
          product_name: campaignData.product_name ?? '',
          product_image_url: campaignData.product_image_url || '',
          // null 을 그대로 넣으면 zod 숫자 필드가 거부한다 (undefined 만 통과)
          product_price: campaignData.product_price ?? undefined,
          product_url: campaignData.product_url || '',
          product_no: campaignData.product_no ?? undefined,
          // 미입력이면 빈 칸으로 둔다 (0 을 넣으면 채워진 것처럼 보인다)
          recruit_count: campaignData.recruit_count ?? (undefined as unknown as number),
          recruit_start_at: isoToDateStr(campaignData.recruit_start_at),
          recruit_end_at: isoToDateStr(campaignData.recruit_end_at),
          selection_announce_at: isoToDateStr(campaignData.selection_announce_at),
          campaign_end_at: isoToDateStr(campaignData.campaign_end_at ?? undefined),
          social_media_channels: campaignData.social_media_channels || {},
          mission_deadline_at: isoToDateStr(campaignData.mission_deadline_at),
          field_config: campaignData.field_config || { ...DEFAULT_FIELD_CONFIG },
          extra_questions: campaignData.extra_questions || [],
          login_required: campaignData.login_required,
          auto_coupon: campaignData.auto_coupon || false,
          skip_shipping_coupon: campaignData.skip_shipping_coupon || false,
          allowed_variants: campaignData.allowed_variants || undefined,
        }

        // react-hook-form에 초기 데이터 설정
        form.reset(prefillData)

        // 상품 선택 상태 복원
        const prodState: Partial<ProductSelectionState> = {
          selectedBasePrice: campaignData.product_price || 0,
        }
        if (campaignData.allowed_variants && campaignData.allowed_variants.length > 0) {
          const av = campaignData.allowed_variants
          prodState.selectedHasOptions = true
          prodState.selectedOptionCount = av.length
          if (av.length === 1) {
            prodState.selectedVariantCode = av[0].variant_code
            prodState.selectedVariantLabel = av[0].label || av[0].variant_code
            prodState.selectedAdditionalAmount = av[0].additional_amount || 0
          }
        } else if (campaignData.product_variant_code) {
          prodState.selectedVariantCode = campaignData.product_variant_code
          prodState.selectedHasOptions = true
        }

        // 상품 API 재조회 -- 할인가 정보 확인
        if (campaignData.product_no) {
          try {
            // Orval 생성 함수로 상품 조회 (imperative 호출)
            const productResponse = await getProducts({
              search: String(campaignData.product_no),
              limit: 1,
            })
            // customInstance가 JSON body를 직접 반환하므로 래퍼 없이 접근
            const data = productResponse as any
            const productList = 'products' in data ? data.products : []
            const product = productList?.find((p: { product_no: number; price: number; discount_price?: number | null; shipping_fee_by_product?: string | null; shipping_fee_type?: string | null }) => p.product_no === campaignData.product_no)
            if (product) {
              prodState.selectedBasePrice = product.price
              if (product.discount_price != null && product.discount_price < product.price) {
                prodState.selectedDiscountPrice = product.discount_price
              }
              // 개별배송 여부 (배송비 쿠폰 적용 가능 여부 판단 — 수정 페이지에서도 선택불가/안내)
              prodState.shippingFeeByProduct = product.shipping_fee_by_product ?? null
              prodState.shippingFeeType = product.shipping_fee_type ?? null
            }
          } catch (e) {
            console.warn('상품 할인 정보 조회 실패:', e)
          }
        }
        setInitialProductSelection(prodState)

        // 썸네일이 상품 이미지와 동일하면 "상품 대표이미지 사용" 체크 ON
        if (
          campaignData.thumbnail_url &&
          campaignData.product_image_url &&
          campaignData.thumbnail_url === campaignData.product_image_url
        ) {
          setInitialUseProductImage(true)
        }

        // 미션 차수 설정 pre-fill
        if (campaignData.mission_rounds && campaignData.mission_rounds.length > 0) {
          setInitialRounds(campaignData.mission_rounds.map((r) => ({
            title: r.title,
            guideline: r.guideline || '',
            guideline_files: r.guideline_files || [],
            deadline_at: isoToDateStr(r.deadline_at),
            mission_type: (r.mission_type || 'both') as 'media' | 'link' | 'both',
          })))
        }

        setDataReady(true)
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : '캠페인 정보를 불러오는데 실패했어요.'
        console.error('Failed to load campaign:', err)
        setError(message)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [campaignId, navigate])

  // 폼 제출 -- 캠페인 수정
  // 반환값 = "저장이 실제로 됐는가" (CampaignForm 이 이탈 가드 기준선 갱신에 쓴다).
  // 여기서는 게이트 422 도 저장 실패다 — update 요청 자체가 거부되므로 아무것도 안 바뀐다.
  // 생성(new.tsx)은 create 성공 후 publish 만 실패하는 경우가 있어 그쪽과 판정이 다르다.
  const handleSubmit = async (
    formData: CampaignFormData,
    missionRounds: MissionRound[],
  ): Promise<boolean | void> => {
    // 상품/가격 변경 시 쿠폰 삭제 경고
    if (campaign && formData.auto_coupon && (campaign.variant_coupons?.length || campaign.product_coupon_no)) {
      const productChanged = formData.product_no != null && campaign.product_no != null && formData.product_no !== campaign.product_no
      const existingCouponPrices = new Set((campaign.variant_coupons || []).map(vc => vc.price))
      // 옵션 있는 상품은 allowed_variants 기준, 옵션 없는 상품은 product_price 단일 항목으로 비교
      // (백엔드 helpers.py auto_create_coupons 가 variant_coupons 를 생성하는 규칙과 동일)
      const newPriceList = formData.allowed_variants && formData.allowed_variants.length > 0
        ? formData.allowed_variants.map(v => v.price)
        : (formData.product_price != null ? [formData.product_price] : [])
      const newPrices = new Set(newPriceList)
      const priceChanged = existingCouponPrices.size > 0 && (
        existingCouponPrices.size !== newPrices.size ||
        [...existingCouponPrices].some(p => !newPrices.has(p))
      )

      if (productChanged || priceChanged) {
        const confirmed = await confirm(
          '상품 또는 옵션 가격이 변경되었어요.\n\n' +
          '기존 쿠폰이 삭제되고 새로운 가격으로 쿠폰이 재생성됩니다.\n' +
          '이미 쿠폰을 발급받은 참여자가 있다면, 참여자 관리에서 개별 재발급이 필요합니다.\n\n' +
          '계속하시겠습니까?'
        )
        if (!confirmed) return
      }
    }

    setIsSaving(true)
    try {
      // 마감일이 정해진 차수 중 마지막 것 (draft 는 마감일 미정 차수를 허용한다)
      const deadlineAt = missionRounds
        .map((r) => r.deadline_at)
        .filter((d): d is string => !!d)
        .sort()
        .pop()

      // 날짜는 **null 로 보내야** 서버에서도 지워진다 — undefined 는 partial update 에서
      // 통째로 제외돼 예전 값이 그대로 남는다. 임시 저장 캠페인은 일정을 비운 채 저장할
      // 수 있어야 하고, 날짜 입력에는 clear 버튼이 있어 실제로 비우는 경로가 있다.
      //
      // 상품 계열은 일부러 undefined 를 유지한다. null 로 바꿨더니 옵션이 2개 이상인
      // 캠페인에서 productSelection.selectedVariantCode 가 복원되지 않아
      // product_variant_code 가 **저장할 때마다 지워지는** 회귀가 났다. draft 에서
      // 상품을 비우는 것은 아직 서버에 반영되지 않는다(기존 동작 유지).
      const requestData: SeedingCampaignUpdateRequest = {
        title: formData.title!,
        description: formData.description || undefined,
        notices: formData.notices || undefined,
        detail_images: formData.detail_images?.length ? formData.detail_images : undefined,
        delivery_method: formData.delivery_method || 'cafe24_order',
        product_name: formData.product_name || undefined,
        product_price: formData.product_price,
        product_url: formData.product_url || undefined,
        product_no: formData.product_no,
        product_variant_code: formData.product_variant_code || undefined,
        allowed_variants: formData.allowed_variants || undefined,
        thumbnail_url: formData.thumbnail_url || undefined,
        product_image_url: formData.product_image_url || undefined,
        recruit_count: formData.recruit_count || null,
        recruit_start_at: dateStrToIso(formData.recruit_start_at, '00:00:00') ?? null,
        recruit_end_at: dateStrToIso(formData.recruit_end_at) ?? null,
        selection_announce_at: dateStrToIso(formData.selection_announce_at) ?? null,
        social_media_channels: formData.social_media_channels,
        mission_deadline_at: deadlineAt ?? null,
        mission_rounds: missionRounds,
        campaign_end_at: dateStrToIso(formData.campaign_end_at) ?? null,
        field_config: formData.field_config || DEFAULT_FIELD_CONFIG,
        extra_questions: (formData.extra_questions && formData.extra_questions.length > 0)
          ? formData.extra_questions
          : undefined,
        login_required: formData.login_required ?? true,
        auto_coupon: formData.delivery_method === 'self_delivery' ? false : (formData.auto_coupon ?? false),
        skip_shipping_coupon: formData.skip_shipping_coupon || false,
      }

      try {
        await updateCampaignMutation.mutateAsync({ campaignId, data: requestData })
      } catch (err: unknown) {
        // 공개된 캠페인은 연락처가 계속 등록돼 있어야 수정 가능 → 미등록이면 게이트 안내
        const gateMsg = extractGateMessage(err)
        if (gateMsg) {
          setGateMessage(gateMsg)
          return
        }
        throw err // 게이트 외 에러는 CampaignForm이 표시하도록 재전파
      }

      // 캠페인 상세 + 목록 캐시 무효화 (수정 내용 즉시 반영)
      queryClient.invalidateQueries({ queryKey: getGetCampaignQueryKey(campaignId) })
      queryClient.invalidateQueries({ queryKey: getListCampaignsQueryKey() })

      navigate({
        to: '/seeding-campaigns/$campaignId',
        params: { campaignId },
      })
      return true
    } finally {
      setIsSaving(false)
    }
  }

  // 수정 모드 쿠폰 미리보기 렌더러 (기존 쿠폰이 있으면 기존 정보 표시)
  const renderCouponPreview = (
    formData: CampaignFormValues,
    handlers: { onSkipShippingChange: (value: boolean) => void },
  ) => {
    if (!formData.auto_coupon || !formData.product_no) return null

    // 기존 쿠폰이 생성되어 있으면 기존 정보 표시
    if (campaign?.variant_coupons?.length || campaign?.product_coupon_no || campaign?.shipping_coupon_no) {
      return (
        <div className="mt-3 ml-6 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* 상품 쿠폰 카드: variant_coupons 기반 */}
            {(campaign?.variant_coupons && campaign.variant_coupons.length > 0) ? (
              campaign.variant_coupons.map((vc, idx) => (
                <div key={idx} className="border border-border rounded-lg p-3">
                  <p className="text-xs font-medium text-on-surface-variant mb-1">상품 할인 쿠폰</p>
                  <p className="text-sm font-semibold text-on-surface">{vc.price?.toLocaleString()}원 할인</p>
                  {vc.variant_codes?.length > 0 && campaign.allowed_variants && (
                    <p className="text-xs text-outline-variant mt-0.5">
                      {vc.variant_codes.map((code: string) =>
                        campaign.allowed_variants?.find(v => v.variant_code === code)?.label || code
                      ).join(', ')}
                    </p>
                  )}
                  <p className="text-xs text-green-600 mt-1">생성됨</p>
                </div>
              ))
            ) : campaign?.product_coupon_no ? (
              <div className="border border-border rounded-lg p-3">
                <p className="text-xs font-medium text-on-surface-variant mb-1">상품 할인 쿠폰</p>
                <p className="text-sm font-semibold text-on-surface">{campaign.product_price?.toLocaleString()}원 할인</p>
                <p className="text-xs text-green-600 mt-1">생성됨</p>
              </div>
            ) : (
              <div className="border border-border rounded-lg p-3">
                <p className="text-xs font-medium text-on-surface-variant mb-1">상품 할인 쿠폰</p>
                <p className="text-sm text-outline-variant">미생성</p>
              </div>
            )}
            {/* 배송비 쿠폰 */}
            <div className={`border rounded-lg p-3 transition-colors ${
              formData.skip_shipping_coupon ? 'border-dashed border-border bg-surface' : 'border-border'
            }`}>
              <p className="text-xs font-medium text-on-surface-variant mb-1">배송비 쿠폰</p>
              {formData.skip_shipping_coupon ? (
                <p className="text-sm text-outline-variant">생성 안 함</p>
              ) : campaign?.shipping_coupon_no ? (
                <>
                  <p className="text-sm font-semibold text-on-surface">배송비 전액 무료</p>
                  <p className="text-xs text-green-600 mt-1">생성됨</p>
                </>
              ) : (
                <p className="text-sm text-outline-variant">저장 시 생성됩니다</p>
              )}
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.skip_shipping_coupon || false}
                  onChange={(e) => handlers.onSkipShippingChange(e.target.checked)}
                  className="w-3.5 h-3.5 text-primary rounded border-border focus:ring-ring"
                />
                <span className="text-xs text-on-surface-variant">무료배송 상품의 경우 체크해주세요</span>
              </label>
            </div>
          </div>
          {/* 상품 변경 시 쿠폰 재발급 안내 */}
          {formData.product_no != null && campaign?.product_no != null && formData.product_no !== campaign.product_no && (
            <div className="text-xs text-primary bg-primary/10 p-3 rounded mt-3">
              <p>상품이 변경되었어요. 저장 시 변경된 상품 가격으로 쿠폰이 새로 생성됩니다.</p>
            </div>
          )}
          {/* 할인 행사 경고 */}
          {initialProductSelection.selectedDiscountPrice != null && (
            <div className="text-xs text-amber-600 bg-amber-50 p-3 rounded mt-3">
              <p className="font-medium">현재 할인 행사가 진행 중입니다. 할인가 기준으로 쿠폰이 생성되며, 할인 종료 시 가격이 변경되어 쿠폰 사용에 문제가 될 수 있습니다.</p>
            </div>
          )}
        </div>
      )
    }

    // 기존 쿠폰 없음 -> null 반환하면 CampaignForm의 기본 쿠폰 미리보기가 표시됨
    return null
  }

  // 로딩 중
  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-surface-high rounded w-48 mb-6"></div>
          <div className="h-96 bg-surface-high rounded"></div>
        </div>
      </div>
    )
  }

  // 에러 또는 캠페인 없음
  if (!campaign) {
    return (
      <div className="text-center py-12">
        <span className="text-4xl block mb-4">&#9888;&#65039;</span>
        <div className="text-on-surface-variant text-lg">
          {error || '캠페인을 찾을 수 없어요.'}
        </div>
        <Button
          className="mt-4"
          onClick={() => navigate({ to: '/seeding-campaigns' })}
        >
          목록으로
        </Button>
      </div>
    )
  }

  // dataReady 전에는 CampaignForm을 렌더링하지 않음 (초기 데이터 설정 완료 대기)
  if (!dataReady || !shop) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-surface-high rounded w-48 mb-6"></div>
          <div className="h-96 bg-surface-high rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-on-surface">캠페인 수정</h1>
            <p className="mt-1 text-on-surface-variant">{campaign.title}</p>
            {isCompleted && (
              <p className="mt-1 text-sm text-error">
                종료된 캠페인은 수정할 수 없어요.
              </p>
            )}
            {isLocked && !isCompleted && (
              <p className="mt-1 text-sm text-amber-600">
                모집 시작 이후에는 일부 항목만 수정할 수 있습니다.
              </p>
            )}
          </div>
        </div>

        <CampaignForm
          mode="edit"
          form={form}
          shop={shop}
          campaign={campaign}
          initialRounds={initialRounds}
          initialProductSelection={initialProductSelection}
          initialUseProductImage={initialUseProductImage}
          onSubmit={handleSubmit}
          onCancel={() => navigate({
            to: '/seeding-campaigns/$campaignId',
            params: { campaignId },
          })}
          isSaving={isSaving}
          lockState={{
            isLocked,
            isRecruiting,
            isPostRecruit,
            isCompleted,
            hasApplicants,
            originalQuestionIds,
          }}
          renderCouponPreview={renderCouponPreview}
          previewCampaignId={campaignId}
        />
      </div>

      {/* 공개 게이트 안내 — 연락처 미등록 시 등록 유도 (닫으면 다시 저장 시도) */}
      <SeedingPublishGate message={gateMessage} onClose={() => setGateMessage(null)} />

      <ConfirmDialog />
    </>
  )
}
